import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast, Toaster } from "sonner";

import SectionHeading from "./SectionHeading";
import MagneticButton from "./MagneticButton";
import { company, services } from "../data/site";
import { API_URL } from "../config";
import useDynamicContent from "../hooks/useDynamicContent";
import useSiteSettings from "../hooks/useSiteSettings";

const API = API_URL;

export default function ContactSection({ embedded = false }) {
  const { content } = useDynamicContent('contact');
  const { getSetting } = useSiteSettings();
  
  const formHeading = content.form_heading || "Let’s simplify your next approval.";
  const formSubheading = content.form_subheading || "Tell us a little about your project — we’ll return with a clear path, a realistic timeline, and a single point of accountability.";
  
  const address = getSetting('company_address', company.address);
  const phone = getSetting('company_phone', company.phone);
  const phoneAlt = getSetting('company_phone_alt', company.phoneAlt);
  const email = getSetting('company_email', company.email);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: services[0]?.code || "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    try {
      setStatus("sending");
      await axios.post(`${API}/leads`, form);
      setStatus("success");
      toast.success("Thank you — we'll respond within one business day.");
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: services[0]?.code || "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      setStatus("error");
      toast.error("Something went wrong. Please try again or call us directly.");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className={`relative px-6 md:px-12 ${embedded ? "pb-32" : "py-28 md:py-40"}`}
    >
      {!embedded && <Toaster position="top-center" theme="dark" richColors />}
      <div className="absolute -top-32 right-0 h-[400px] w-[400px] orange-glow opacity-50" />

      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="Start a Conversation"
            title={formHeading}
            description={formSubheading}
            testId="contact-heading"
          />

          <ul className="mt-10 space-y-5 text-white/70">
            <li className="flex gap-4">
              <span className="h-11 w-11 rounded-full border border-white/10 grid place-items-center text-[#C96A2B] shrink-0">
                <MapPin size={18} />
              </span>
              <div>
                <p className="text-[11px] tracking-[0.3em] uppercase text-white/40">
                  Office
                </p>
                <p className="mt-1 text-white">{address}</p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="h-11 w-11 rounded-full border border-white/10 grid place-items-center text-[#C96A2B] shrink-0">
                <Phone size={18} />
              </span>
              <div>
                <p className="text-[11px] tracking-[0.3em] uppercase text-white/40">
                  Phone
                </p>
                <a
                  href={`tel:${phone.replace(/\s/g, "")}`}
                  className="mt-1 text-white block"
                >
                  {phone}
                </a>
                <a
                  href={`tel:${phoneAlt.replace(/\s/g, "")}`}
                  className="text-white/60 text-sm"
                >
                  {phoneAlt}
                </a>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="h-11 w-11 rounded-full border border-white/10 grid place-items-center text-[#C96A2B] shrink-0">
                <Mail size={18} />
              </span>
              <div>
                <p className="text-[11px] tracking-[0.3em] uppercase text-white/40">
                  Email
                </p>
                <a href={`mailto:${email}`} className="mt-1 text-white block">
                  {email}
                </a>
              </div>
            </li>
          </ul>

          <div className="mt-10 rounded-2xl overflow-hidden border border-white/10 h-56">
            <iframe
              title="Hoflich Hyderabad Office"
              src={company.mapEmbed}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-full grayscale-[60%] contrast-110"
              data-testid="contact-map"
            />
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7 glass rounded-3xl p-8 md:p-12"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Your name" value={form.name} onChange={update("name")} required testId="form-name" />
            <Field label="Email" type="email" value={form.email} onChange={update("email")} required testId="form-email" />
            <Field label="Phone" value={form.phone} onChange={update("phone")} testId="form-phone" />
            <Field label="Company" value={form.company} onChange={update("company")} testId="form-company" />

            <div className="md:col-span-2">
              <label className="block text-[11px] tracking-[0.3em] uppercase text-white/45 mb-3">
                Service of interest
              </label>
              <div className="flex flex-wrap gap-2">
                {services.map((s) => (
                  <button
                    type="button"
                    key={s.code}
                    onClick={() => setForm((f) => ({ ...f, service: s.code }))}
                    data-testid={`form-service-${s.code.toLowerCase()}`}
                    className={`px-4 py-2 rounded-full border text-xs tracking-wider transition-colors ${
                      form.service === s.code
                        ? "bg-[#C96A2B] border-[#C96A2B] text-black"
                        : "border-white/15 text-white/70 hover:border-[#C96A2B] hover:text-[#C96A2B]"
                    }`}
                  >
                    {s.code}
                  </button>
                ))}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-[11px] tracking-[0.3em] uppercase text-white/45 mb-3">
                Tell us about your project
              </label>
              <textarea
                value={form.message}
                onChange={update("message")}
                required
                rows={5}
                data-testid="form-message"
                placeholder="Site location, typology, approvals needed, timeline..."
                className="w-full bg-transparent border-b border-white/15 focus:border-[#C96A2B] outline-none py-3 text-white placeholder:text-white/30 resize-none transition-colors"
              />
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
            <p className="text-xs text-white/40 max-w-sm">
              By submitting, you agree to be contacted by Hoflich regarding your enquiry.
              We respect your privacy.
            </p>
            <MagneticButton
              type="submit"
              testId="form-submit"
              onClick={submit}
            >
              {status === "sending" && (
                <Loader2 size={16} className="animate-spin" />
              )}
              {status === "success" && <CheckCircle2 size={16} />}
              {status === "error" && <AlertTriangle size={16} />}
              {status === "sending"
                ? "Sending..."
                : status === "success"
                ? "Sent"
                : "Send Enquiry"}
            </MagneticButton>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.3em] uppercase text-white/45 mb-3">
        {label} {required && <span className="text-[#C96A2B]">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="w-full bg-transparent border-b border-white/15 focus:border-[#C96A2B] outline-none py-3 text-white placeholder:text-white/30 transition-colors"
      />
    </div>
  );
}
